/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {
  BayLevelEnum,
  ForeAftEnum,
  IBayLevelData,
  IBayLevelOffsetBottomBase,
  IMasterCGs,
  IOpenVesselDefinitionV1,
  ISizeSummary,
  TContainerLengths,
  ValuesSourceEnum,
} from "open-vessel-definition";
import {
  IBayPattern,
  IRowPattern,
  ISlotPattern,
  ITierPattern,
} from "../types/IPositionPatterns";
import {
  IGetPairedBaysOutput,
  getAllThePairedBays,
} from "../getAllThePairedBays";
import {
  IMostObservedTiersAboveAndBelow,
  calculateMissingBottomBases,
} from "./calculateMissingBottomBases";
import { TAllBayLcgs, calculateMissingLcgs } from "./calculateMissingLcgs";
import {
  cloneObject,
  createDictionary,
  feetToMillimeters,
  pad3,
} from "@tedivo/tedivo-pure-helpers";

import { BaySizesAndCgs } from "./BaySizesAndCgs";
import { I20LcgsByBay } from "../types/I20Lcgs";
import { IBayBlocksAndSizes } from "../types/IBayBlocksAndSizes";
import { calculateBottomBasesByBay } from "./calculateBottomBasesByBay";
import { calculateMissingTcgs } from "./calculateMissingTcgs";
import generate20Lcgs from "../generate20Lcgs";
import { getBayBlocks } from "../getBayBlocks";
import { getRowsAndTiersFromSlotKeys } from "../getRowsAndTiersFromSlotKeys";
import { getSizesFromSlotDataArray } from "../getSizesFromSlots";

export class BayLcgVcgTcgAndPairings {
  private options: IBayLcgVcgAndPairingsOptions;

  /** Total Bays in the ship */
  readonly maxIsoBay: number;
  /** All isoBays in an array for calculations */
  readonly allIsoBays: {
    isoBay: `${number}${number}${number}`;
  }[];

  /** Calculation of paired and unpaired bays by level */
  readonly pairedBaysCalc: IGetPairedBaysOutput;
  /** Array of blocks of paired/unpaired bays and sizes */
  readonly blockBaysAndSizes: IBayBlocksAndSizes[];
  /** Object of blocks of paired/unpaired bays and sizes */
  readonly blockBaysAndSizesByBlockBay: Record<string, IBayBlocksAndSizes>;
  /** Object of blocks of paired/unpaired bays and sizes by 20 ft bays */
  readonly blockBaysAndSizesBy20Bay: Record<
    `${number}${number}${number}`,
    IBayBlocksAndSizes
  >;

  readonly lcgsBy20Bay: I20LcgsByBay;

  /** Above (Deck) bottom bases by bay */
  readonly aboveBottomBases: Record<IBayPattern, number>;
  /** Below bottom bases by bay */
  readonly belowBottomBases: Record<IBayPattern, number>;

  readonly bayLevelPositionsAbove: BaySizesAndCgs[];
  readonly bayLevelPositionsBelow: BaySizesAndCgs[];
  readonly bayLevelPositionsDict: Record<
    `${IBayPattern}-${BayLevelEnum}`,
    BaySizesAndCgs
  >;

  readonly missingImportantLcgs: boolean;
  readonly missingImportantVcgs: boolean;
  readonly missingImportantTcgs: boolean;

  readonly minBottomBase: number;
  readonly maxBottomBase: number;
  readonly minLcg: number;
  readonly maxLcg: number;
  readonly minTcg: number;
  readonly maxTcg: number;

  readonly cgStats: {
    readonly countLcgs: number;
    readonly definedLcgs: number;
    readonly countTcgs: number;
    readonly definedTcgs: number;
    readonly countBottomBase: number;
    readonly definedBottomBase: number;
    readonly totalSlotsCount: number;
  };

  readonly minTierAbove = Infinity;
  readonly maxTierAbove = -Infinity;

  readonly tiersBoundaries: IMostObservedTiersAboveAndBelow;

  readonly missingTcgs: Record<IRowPattern, number>;
  readonly missingBottomBases: Record<ITierPattern, number>;
  readonly missingLcgs: TAllBayLcgs;

  readonly sizeSummary: ISizeSummary;

  constructor(
    json: IOpenVesselDefinitionV1,
    options: IBayLcgVcgAndPairingsOptions = {
      averageContainerHeight: 8.5,
    },
  ) {
    this.options = options;

    const {
      baysData,
      vesselPartsData,
      sizeSummary,
      shipData: { masterCGs },
    } = json;

    this.sizeSummary = sizeSummary;

    const lcgByBayAbove: BaySizesAndCgs[] = [];
    const lcgByBayBelow: BaySizesAndCgs[] = [];

    let _minBottomBase = Infinity,
      _maxBottomBase = -Infinity,
      _minLcg = Infinity,
      _maxLcg = -Infinity,
      _minTcg = Infinity,
      _maxTcg = -Infinity,
      _minTierAbove = Infinity,
      _maxTierAbove = -Infinity,
      _maxIsoBay = 0,
      _countLcgs = 0,
      _definedLcgs = 0,
      _countTcgs = 0,
      _definedTcgs = 0,
      _countBottomBase = 0,
      _definedBottomBase = 0,
      _totalSlotsCount = 0;

    baysData
      .slice()
      .sort(sortByBayAsc)
      .forEach((bl) => {
        const output = new BaySizesAndCgs(bl, sizeSummary, masterCGs);

        const isAbove = bl.level === BayLevelEnum.ABOVE;

        if (_maxIsoBay < Number(bl.isoBay)) _maxIsoBay = Number(bl.isoBay);

        if (_minTcg > output.minTcg) _minTcg = output.minTcg;
        if (_maxTcg < output.maxTcg) _maxTcg = output.maxTcg;
        if (_minLcg > output.minLcg) _minLcg = output.minLcg;
        if (_maxLcg < output.maxLcg) _maxLcg = output.maxLcg;
        if (_minBottomBase > output.minBottomBase)
          _minBottomBase = output.minBottomBase;
        if (_maxBottomBase < output.maxBottomBase)
          _maxBottomBase = output.maxBottomBase;

        isAbove ? lcgByBayAbove.push(output) : lcgByBayBelow.push(output);

        _countLcgs += output.countLcgs;
        _definedLcgs += output.definedLcgs;
        _countTcgs += output.countTcgs;
        _definedTcgs += output.definedTcgs;
        _countBottomBase += output.countBottomBase;
        _definedBottomBase += output.definedBottomBase;
        _totalSlotsCount += output.slotsKeys.length;

        if (isAbove) {
          if (_minTierAbove > output.minTier) _minTierAbove = output.minTier;
          if (_maxTierAbove < output.maxTier) _maxTierAbove = output.maxTier;
        }
      });

    findPairedBay40CheckingTiers(lcgByBayAbove);
    findPairedBay40CheckingTiers(lcgByBayBelow);

    const missingImportantLcgs = isMissingImportantXcgs(
      lcgByBayAbove,
      lcgByBayBelow,
      "missingImportantLcgs",
    );

    const missingImportantVcgs = isMissingImportantXcgs(
      lcgByBayAbove,
      lcgByBayBelow,
      "missingImportantVcgs",
    );

    const missingImportantTcgs = isMissingImportantXcgs(
      lcgByBayAbove,
      lcgByBayBelow,
      "missingImportantTcgs",
    );

    const pairedBaysCalc = getAllThePairedBays(baysData, false);
    this.pairedBaysCalc = pairedBaysCalc;

    this.bayLevelPositionsDict = createDictionary(
      [...lcgByBayAbove, ...lcgByBayBelow],
      (bsc) => `${bsc.isoBay}-${bsc.level}`,
    );

    const {
      allIsoBays,
      blockBaysAndSizes,
      blockBaysAndSizesByBlockBay,
      blockBaysAndSizesBy20Bay,
    } = getBayBlocks(baysData, pairedBaysCalc);

    this.allIsoBays = allIsoBays;
    this.blockBaysAndSizes = blockBaysAndSizes;
    this.blockBaysAndSizesByBlockBay = blockBaysAndSizesByBlockBay;
    this.blockBaysAndSizesBy20Bay = blockBaysAndSizesBy20Bay;

    /* Missing TCGs ? */
    const missingTcgsInfo = calculateMissingTcgs(
      missingImportantTcgs,
      sizeSummary,
    );

    this.missingTcgs = missingTcgsInfo.allRowsTcgs;
    if (missingTcgsInfo.calcMinTcg !== undefined)
      _minTcg = missingTcgsInfo.calcMinTcg;
    if (missingTcgsInfo.calcMaxTcg !== undefined)
      _maxTcg = missingTcgsInfo.calcMaxTcg;

    /* Missing Bottom Bases ? */
    const { missingBottomBases, tiersBoundaries } = calculateMissingBottomBases(
      missingImportantVcgs,
      sizeSummary,
      pairedBaysCalc,
      this.bayLevelPositionsDict,
    );

    this.missingBottomBases = missingBottomBases;
    this.tiersBoundaries = tiersBoundaries;

    /* Missing LCGs ? */
    const { missingLcgs } = calculateMissingLcgs({
      missingImportantLcgs,
      allIsoBays,
      blockBaysAndSizes,
      blockBaysAndSizesByBlockBay,
      blockBaysAndSizesBy20Bay,
      vesselPartsData,
      containerLengths: json.shipData.containersLengths ?? [],
    });

    this.missingLcgs = missingLcgs;

    const { lcgsBy20Bay, minLcgs20, maxLcgs20 } = generate20Lcgs({
      maxIsoBay: _maxIsoBay,
      lcgByBayAbove,
      lcgByBayBelow,
      missingImportantLcgs,
      missingLcgs,
      blockBaysAndSizesBy20Bay,
    });

    this.lcgsBy20Bay = lcgsBy20Bay;
    if (_minLcg > minLcgs20) _minLcg = minLcgs20;
    if (_maxLcg < maxLcgs20) _maxLcg = maxLcgs20;

    /* Calculate the absolute ship deck height for each bay */
    const calcsBottomBases = calculateBottomBasesByBay({
      blps: [...lcgByBayAbove, ...lcgByBayBelow],
      missingBottomBases: missingImportantVcgs ? missingBottomBases : {},
    });

    this.aboveBottomBases = calcsBottomBases.aboveBottomBases;
    this.belowBottomBases = calcsBottomBases.belowBottomBases;

    // Assign the values to the class
    this.bayLevelPositionsAbove = lcgByBayAbove;
    this.bayLevelPositionsBelow = lcgByBayBelow;

    this.missingImportantLcgs = missingImportantLcgs;
    this.missingImportantVcgs = missingImportantVcgs;
    this.missingImportantTcgs = missingImportantTcgs;

    this.minBottomBase = _minBottomBase;
    this.maxBottomBase = _maxBottomBase;
    this.minLcg = _minLcg;
    this.maxLcg = _maxLcg;
    this.minTcg = _minTcg;
    this.maxTcg = _maxTcg;

    this.cgStats = {
      countLcgs: _countLcgs,
      definedLcgs: _definedLcgs,
      countTcgs: _countTcgs,
      definedTcgs: _definedTcgs,
      countBottomBase: _countBottomBase,
      definedBottomBase: _definedBottomBase,
      totalSlotsCount: _totalSlotsCount,
    };

    this.minTierAbove = _minTierAbove;
    this.maxTierAbove = _maxTierAbove;
    this.maxIsoBay = _maxIsoBay;

    function findPairedBay40CheckingTiers(bayInfo: BaySizesAndCgs[] = []) {
      for (let bayIndex = 0; bayIndex < bayInfo.length; bayIndex += 1) {
        const dta = bayInfo[bayIndex];

        if (dta.pairedBay === undefined) continue;

        let someTierInPairedBayHas40 = false;

        const potentialSiblingBay =
          bayInfo[bayIndex + (dta.pairedBay === ForeAftEnum.AFT ? 1 : -1)];

        if (
          potentialSiblingBay &&
          potentialSiblingBay.pairedBay !== dta.pairedBay // Wrong if both are paired in the same direction
        ) {
          dta.pairedToBay = potentialSiblingBay.isoBay;

          if (potentialSiblingBay.getMaxSizeInTiers(dta.getAllTiers()) >= 40)
            someTierInPairedBayHas40 = true;
        }

        dta.someTierInPairedBayHas40 = someTierInPairedBayHas40;
      }
    }
  }

  public getMaxTheoreticalHeightInShip(
    /** Provide a theoretical container height to measure total height. Default is 8.5 ft */
    theoreticalContainerHeightInFeet = 8.5,
  ): number {
    const minTierAbove = this.minTierAbove;
    const maxTierAbove = this.maxTierAbove;
    const tiersBoundaries = this.tiersBoundaries;

    if (minTierAbove !== Infinity && maxTierAbove !== -Infinity) {
      const numTiers = Math.floor((maxTierAbove - minTierAbove) * 0.5) + 1;
      const maxHeight =
        numTiers * feetToMillimeters(theoreticalContainerHeightInFeet);
      const maxAboveBaseBottom = Math.max(
        ...Object.values(this.aboveBottomBases).filter(Boolean),
      );
      return maxAboveBaseBottom + maxHeight;
    } else if (tiersBoundaries.maxNumberOfTiersBelow.n !== 0) {
      return (
        tiersBoundaries.maxNumberOfTiersBelow.n *
        feetToMillimeters(theoreticalContainerHeightInFeet)
      );
    }

    return 0;
  }

  get maxHeight(): number | undefined {
    return this.getMaxTheoreticalHeightInShip(
      this.options?.averageContainerHeight ?? 8.5,
    );
  }

  getMissingTcg(row: IRowPattern): number | undefined {
    return this.missingTcgs[row];
  }

  getMissingBottomBase(tier: ITierPattern): number | undefined {
    return this.missingBottomBases[tier];
  }

  getMissing20Lcg(
    bay: IBayPattern,
    len: TContainerLengths,
  ): number | undefined {
    return this.missingLcgs[bay]?.get?.(len)?.lcg;
  }

  /** Adds artificial CGs to the json
  * @param json - The JSON object to modify
  * @returns The modified JSON cloned object with artificial CGs added
  * @remarks
  This method will add artificial LCGs, TCGs, and Bottom Bases to the
  JSON object if there are missing CGs or vessel parts have been added.
  It will also remove perRowInfo for each bay to avoid inconsistencies.
  */
  addArtificialCgsToJson(
    json: IOpenVesselDefinitionV1,
    offsetBottomBases: IBayLevelOffsetBottomBase[] | undefined,
  ): IOpenVesselDefinitionV1 {
    const jsonWithArtificialCgs = cloneObject(json);
    const sizeSummary = jsonWithArtificialCgs.sizeSummary;
    const baysDataDict: Record<string, IBayLevelData> = createDictionary(
      jsonWithArtificialCgs.baysData,
      (b) => `${b.isoBay}-${b.level}`,
    );

    const artificialMasterCgs: IMasterCGs = {
      aboveTcgs: {
        ...jsonWithArtificialCgs.shipData.masterCGs?.aboveTcgs,
      },
      belowTcgs: {
        ...jsonWithArtificialCgs.shipData.masterCGs?.belowTcgs,
      },
      bottomBases: {
        ...jsonWithArtificialCgs.shipData.masterCGs?.bottomBases,
      },
    };

    function removePerBayAndRowCommonAndEachInfo(
      bl: IBayLevelData | undefined,
    ) {
      if (!bl) return;
      bl.perRowInfo = { common: {} }; // Remove each row info to avoid inconsistencies
    }

    // 1. Add missing LCGs
    if (this.missingImportantLcgs) {
      const addMissingLcgsToBay = (bl: IBayLevelData | undefined) => {
        if (!bl) return;

        if (!bl.infoByContLength) bl.infoByContLength = {};

        // Get the sizes
        const sizes = getSizesFromSlotDataArray(
          Object.values(bl.perSlotInfo || {}),
        );

        if (sizes.length === 0) return;

        // Replace the missing LCGs with artificial ones
        sizes.forEach((len) => {
          const lcg = this.getMissing20Lcg(bl.isoBay, len);

          bl.infoByContLength[len] = bl.infoByContLength[len] || {
            size: len,
          };

          if (lcg !== undefined) {
            bl.infoByContLength[len].lcg = lcg;
          }
        });
      };

      // Add the LCGs to the artificial master CGs
      for (let i = 1; i <= sizeSummary.isoBays; i += 2) {
        const bay = pad3(i);
        addMissingLcgsToBay(baysDataDict[`${bay}-${BayLevelEnum.ABOVE}`]);
        addMissingLcgsToBay(baysDataDict[`${bay}-${BayLevelEnum.BELOW}`]);
      }

      jsonWithArtificialCgs.shipData.lcgOptions.values = ValuesSourceEnum.KNOWN;
    }

    // 2. Add missing TCGs
    if (this.missingImportantTcgs) {
      // 2.1 Remove perRowInfo for each bay to avoid inconsistencies
      for (let i = 1; i <= sizeSummary.isoBays; i += 2) {
        const bay = pad3(i);
        removePerBayAndRowCommonAndEachInfo(
          baysDataDict[`${bay}-${BayLevelEnum.ABOVE}`],
        );
        removePerBayAndRowCommonAndEachInfo(
          baysDataDict[`${bay}-${BayLevelEnum.BELOW}`],
        );
      }

      // 2.2 Add the TCG to the artificial master CGs
      for (const row of Object.keys(this.missingTcgs) as IRowPattern[]) {
        const tcg = this.missingTcgs[row];
        if (tcg === undefined) continue;
        artificialMasterCgs.aboveTcgs[row] = tcg;
        artificialMasterCgs.belowTcgs[row] = tcg;
      }

      jsonWithArtificialCgs.shipData.tcgOptions.values = ValuesSourceEnum.KNOWN;
    }

    // 3. Add missing Bottom Bases
    if (this.missingImportantVcgs) {
      // 3.1 Remove perRowInfo for each bay to avoid inconsistencies
      for (let i = 1; i <= sizeSummary.isoBays; i += 2) {
        const bay = pad3(i);
        removePerBayAndRowCommonAndEachInfo(
          baysDataDict[`${bay}-${BayLevelEnum.ABOVE}`],
        );
        removePerBayAndRowCommonAndEachInfo(
          baysDataDict[`${bay}-${BayLevelEnum.BELOW}`],
        );
      }

      // 3.2 Add the BB to the artificial master CGs
      for (const tier of Object.keys(
        this.missingBottomBases,
      ) as ITierPattern[]) {
        const bb = this.missingBottomBases[tier];
        if (bb === undefined) continue;
        artificialMasterCgs.bottomBases[tier] = bb;
      }

      // 3.3 Add the missing Bottom Bases to each bay
      offsetBottomBases?.forEach((a) => {
        a.bays.forEach((b) => {
          const bl = baysDataDict[`${b}-${a.level}`];
          if (!bl) return;
          const { minTier } = getRowsAndTiersFromSlotKeys(
            Object.keys(bl.perSlotInfo || {}) as ISlotPattern[],
          );
          if (minTier === undefined) return;

          // Remove each row info to avoid inconsistencies
          bl.perRowInfo = {
            common: {
              bottomBase: artificialMasterCgs.bottomBases[minTier] + a.offset,
            },
          };
        });
      });

      jsonWithArtificialCgs.shipData.vcgOptions.values = ValuesSourceEnum.KNOWN;
    }

    jsonWithArtificialCgs.shipData.masterCGs = artificialMasterCgs;

    return jsonWithArtificialCgs;
  }
}

interface IBayLcgVcgAndPairingsOptions {
  averageContainerHeight: number;
}

function isMissingImportantXcgs(
  lcgByBayAbove: BaySizesAndCgs[],
  lcgByBayBelow: BaySizesAndCgs[],
  attr:
    | "missingImportantLcgs"
    | "missingImportantVcgs"
    | "missingImportantTcgs",
) {
  return (
    lcgByBayAbove.reduce<boolean>((acc, y) => acc || y[attr], false) ||
    lcgByBayBelow.reduce<boolean>((acc, y) => acc || y[attr], false)
  );
}

const sortByBayAsc = (a: IBayLevelData, b: IBayLevelData): number =>
  Number(a.isoBay) - Number(b.isoBay);

// const getForeLcg = (maxSize: number, lcg?: number): number | undefined =>
//   lcg !== undefined
//     ? roundDec(lcg + feetToMillimeters(maxSize * 0.5), 2)
//     : undefined;

// const getAftLcg = (maxSize: number, lcg?: number): number | undefined =>
//   lcg !== undefined
//     ? roundDec(lcg - feetToMillimeters(maxSize * 0.5), 2)
//     : undefined;
